import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { fetchUserMe } from 'actions/userMe';
import { Navigate, useNavigate, useLocation } from 'react-router-dom';
import {
  setPassword,
  resetPassword,
  resetExpiredPassword,
  getConfirmation
} from 'actions/users';
import { Password } from 'components/Password';
import ContainerWithLogo from 'components/ContainerWithLogo';
import PhonePrompt from 'components/Users/PhonePrompt';
import Browser from 'helpers/browser';
import i18n from 'helpers/i18n';
import {
  parsePasswordApiErrors,
  describePasswordPolicyCode
} from 'helpers/passwordErrors';
import messages from './messages';
import RouteHelpers from 'helpers/routes';
import {
  redirectAfterAuth as sharedRedirectAfterAuth,
  handleAuthHydrationFailure
} from 'helpers/authRedirect';
import useLocale from 'hooks/useLocale';
import {
  getExpiredPassword,
  clearExpiredPassword
} from 'helpers/expiredPasswordStore';
import { update } from 'store/slices/user';
import queryString from 'query-string';
import { Alert, Text, TextLink } from '@teachstonellc/component-library';
import { toastError } from 'components/Toast';

const steps = {
  SET_PASSWORD: 'setPassword',
  SET_PHONE: 'setPhone'
} as const;

type Step = (typeof steps)[keyof typeof steps];

interface PasswordFormValues {
  password: string;
  password_confirmation: string;
  tos_pp_agreed_at: boolean;
}

interface ExpiredPasswordFormValues {
  username: string;
  expired_password: string;
  new_password: string;
}

interface PasswordFormProps {
  tosAgreed: boolean;
  setTosAgreed: (agreed: boolean) => void;
  tosError: string | null;
  isSubmitting: boolean;
  reset_password_token: string | null;
  handlePasswordSubmit: (values: {
    password: string;
    password_confirmation: string;
  }) => void;
  firstName: string;
  lastName: string;
  email: string;
  expired: boolean;
}

interface SetPasswordLocationState {
  expired?: boolean;
  username?: string;
  redirect_to?: string | null;
  abs_redirect?: string | null;
  skip_onboarding?: boolean;
}

const PasswordForm: React.FC<PasswordFormProps> = ({
  tosAgreed,
  setTosAgreed,
  tosError,
  isSubmitting,
  reset_password_token,
  handlePasswordSubmit,
  firstName,
  lastName,
  email,
  expired
}) => (
  <>
    <Text variant="h3" className="mb-4">
      {i18n.ft(messages.setYourPassword)}
    </Text>

    <Password
      onSubmit={handlePasswordSubmit}
      submitButtonText={i18n.ft(messages.setMyPassword)}
      firstName={firstName}
      lastName={lastName}
      email={email}
      isSubmitting={isSubmitting}
      autoFocusPassword
    >
      {reset_password_token == null && !expired && (
        <div className="my-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={tosAgreed}
              onChange={e => setTosAgreed(e.target.checked)}
              required
            />
            <span>
              {i18n.ft(messages.termsOfService.privacy01)}
              <TextLink
                target="_blank"
                href="http://teachstone.com/terms-conditions/"
                rel="noopener noreferrer"
                className="[&>span]:text-[1rem] [&>span]:font-[proxima-nova]"
              >
                {i18n.ft(messages.termsOfService.privacy02)}
              </TextLink>
              {i18n.ft(messages.termsOfService.privacy03)}
              <TextLink
                target="_blank"
                href="http://teachstone.com/privacy-policy/"
                rel="noopener noreferrer"
                className="[&>span]:text-[1rem] [&>span]:font-[proxima-nova]"
              >
                {i18n.ft(messages.termsOfService.privacy04)}
              </TextLink>
            </span>
          </label>
          {tosError && (
            <div className="text-ds-text-negative text-xs mt-1">{tosError}</div>
          )}
        </div>
      )}
    </Password>
  </>
);

function showPasswordError(err: unknown) {
  const data = (
    err as {
      response?: {
        data?: { error?: unknown; errors?: unknown; error_code?: string };
      };
    }
  )?.response?.data;
  const errors = (data?.errors ?? data?.error) as
    string | Record<string, string[] | string> | undefined;
  const parsedApiError = parsePasswordApiErrors(errors);
  const isTokenError =
    typeof errors === 'object' &&
    errors !== null &&
    'reset_password_token' in errors;

  const policyError = describePasswordPolicyCode(data?.error_code);

  const description = isTokenError
    ? i18n.ft(messages.errors.invalidResetToken)
    : (policyError ?? (parsedApiError || i18n.ft(messages.errors.generic)));

  toastError({ description });
}

function SetPasswordPage() {
  const [redirectToLogin, setRedirectToLogin] = useState(false);
  const [currentStep, setCurrentStep] = useState<Step>(steps.SET_PASSWORD);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [tosError, setTosError] = useState<string | null>(null);
  const [tosAgreed, setTosAgreed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [authenticatedUser, setAuthenticatedUser] = useState<any>(null);

  const locale = useLocale();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const locationState =
    (location.state as SetPasswordLocationState | null) ?? {};
  const expired = Boolean(locationState.expired);
  const username = locationState.username ?? '';
  const redirect_to = locationState.redirect_to ?? '';
  const abs_redirect = locationState.abs_redirect ?? '';
  const skipOnboarding = Boolean(locationState.skip_onboarding);

  const parsed = queryString.parse(location.search);

  const confirmation_token =
    typeof parsed.confirmation_token === 'string'
      ? parsed.confirmation_token
      : null;

  const reset_password_token =
    typeof parsed.reset_password_token === 'string'
      ? parsed.reset_password_token
      : null;

  // The expired password lives in memory only, so a reload, a new tab or a
  // back-navigation after the reset already completed arrives here with
  // nothing to authenticate against. Say so and let the user decide what to do
  // - redirecting to login instead bounces an already-authenticated user
  // straight back out to the dashboard, which reads as the app misbehaving.
  useEffect(() => {
    if (expired && !getExpiredPassword()) {
      toastError({ description: i18n.ft(messages.errors.sessionExpired) });
    }
  }, [expired]);

  useEffect(() => {
    if (confirmation_token) {
      getConfirmation(confirmation_token)
        .then(data => {
          const { first_name, last_name, email } = data.data;
          setFirstName(first_name);
          setLastName(last_name);
          setEmail(email);
        })
        .catch(err => {
          showPasswordError(err);
          setRedirectToLogin(true);
        });
    }
  }, [confirmation_token]);

  function handlePasswordRequest(
    type: 'set' | 'reset',
    token: string,
    values: PasswordFormValues
  ) {
    setIsSubmitting(true);
    const request =
      type === 'set'
        ? setPassword({
            user: {
              password: values.password,
              password_confirmation: values.password_confirmation,
              preferred_language: locale,
              tos_pp_agreed_at: values.tos_pp_agreed_at
            },
            confirmation_token: token
          })
        : resetPassword({
            user: {
              password: values.password,
              password_confirmation: values.password_confirmation,
              reset_password_token: token
            }
          });

    return request
      .then(() => {
        if (type === 'set') {
          setCurrentStep(steps.SET_PHONE);
        } else {
          setRedirectToLogin(true);
        }
      })
      .catch(err => showPasswordError(err))
      .finally(() => {
        setIsSubmitting(false);
      });
  }

  async function redirectAfterAuth(user: any) {
    return sharedRedirectAfterAuth(
      user,
      { redirectTo: redirect_to, absRedirect: abs_redirect },
      { dispatch, navigate }
    );
  }

  function handlePostAuthHydrationFailure() {
    handleAuthHydrationFailure(i18n.ft(messages.errors.generic));
  }

  function handleExpiredPasswordRequest(values: ExpiredPasswordFormValues) {
    setIsSubmitting(true);
    resetExpiredPassword({
      username: values.username,
      expired_password: values.expired_password,
      new_password: values.new_password
    })
      .then((response: any) => {
        const user = response.data;
        clearExpiredPassword();
        if (user.login_phone_prompt && !skipOnboarding) {
          // Defer the post-login redirect until after the phone prompt completes.
          setAuthenticatedUser(user);
          setCurrentStep(steps.SET_PHONE);
          return;
        }
        return redirectAfterAuth(user).catch(handlePostAuthHydrationFailure);
      })
      .catch(err => showPasswordError(err))
      .finally(() => {
        setIsSubmitting(false);
      });
  }

  function submitForm(values: PasswordFormValues) {
    if (confirmation_token) {
      handlePasswordRequest('set', confirmation_token, values);
    } else if (reset_password_token) {
      handlePasswordRequest('reset', reset_password_token, values);
    }
  }

  function handleSetPhone() {
    if (authenticatedUser) {
      redirectAfterAuth(authenticatedUser).catch(
        handlePostAuthHydrationFailure
      );
      return;
    }

    fetchUserMe().then(response => {
      dispatch(update(response.data));
      navigate(RouteHelpers.getPath('account'));
    });
  }

  const loginPath = Browser.containsCookie('course_merchant')
    ? RouteHelpers.getPath('users-cm-login')
    : RouteHelpers.getPath('users-login');

  if (redirectToLogin) {
    return <Navigate replace to={loginPath} />;
  }

  function handlePasswordSubmit(values: {
    password: string;
    password_confirmation: string;
  }) {
    if (isSubmitting) {
      return;
    }

    if (expired) {
      const expiredPassword = getExpiredPassword();

      if (!expiredPassword) {
        toastError({ description: i18n.ft(messages.errors.sessionExpired) });
        return;
      }

      const expiredFormValues: ExpiredPasswordFormValues = {
        username,
        expired_password: expiredPassword,
        new_password: values.password
      };

      handleExpiredPasswordRequest(expiredFormValues);
      return;
    }

    if (reset_password_token == null && !tosAgreed) {
      setTosError(i18n.ft(messages.errors?.checkbox?.required));
      return;
    }
    setTosError(null);
    const formValues: PasswordFormValues = {
      ...values,
      tos_pp_agreed_at: tosAgreed
    };
    submitForm(formValues);
  }

  return (
    <>
      {expired && (
        <div className="w-full max-w-[550px] mx-auto my-4 rounded shadow-sm relative">
          <Alert
            variant="notice"
            title="Your password has expired"
            description="For your security, passwords expire periodically. Set a new password to sign in."
            role="alert"
          />
        </div>
      )}
      <div className="mx-4 relative">
        <ContainerWithLogo>
          {currentStep === steps.SET_PASSWORD ? (
            <PasswordForm
              tosAgreed={tosAgreed}
              setTosAgreed={setTosAgreed}
              tosError={tosError}
              isSubmitting={isSubmitting}
              reset_password_token={reset_password_token}
              handlePasswordSubmit={handlePasswordSubmit}
              firstName={firstName}
              lastName={lastName}
              email={email}
              expired={expired}
            />
          ) : (
            <PhonePrompt onSuccess={handleSetPhone} />
          )}
        </ContainerWithLogo>
      </div>
    </>
  );
}

export default SetPasswordPage;
